import { combineReducers } from 'redux';
import { routerReducer } from 'react-router-redux';

import app from './app/AppReducer';
import property from './property/PropertyReducer';
import gallery from './gallery/GalleryReducer';
import map from './map/MapReducer';
import threeDModel from './threeDModel/ThreeDModelReducer';
import video from './video/VideoReducer';
import matterport from './matterport/MatterportReducer';
import floorplan from './floorplan/FloorplanReducer';
import agent from './agent/AgentReducer';
import listings from './listings/ListingsReducer';
import modals from './modals/ModalsReducer';
import claimHome from './claimHome/ClaimHomeReducer';
import featured from './featured/FeaturedReducer';
import hero from './hero/HeroReducer';

import * as appConsts from './app/AppConsts';

const appReducer = combineReducers({
    app,
    property,
    hero,
    gallery,
    map,
    threeDModel,
    video,
    matterport,
    floorplan,
    agent,
    listings,
    modals,
    claimHome,
    featured,
    routing: routerReducer
});

const rootReducer = (state, action) => {

    const getThreeDModelUrl = (data) => {
        if (data.sd_project_key && data.sd_project_key.length) {
            return data.sd_project_key;
        }

        if (data.floorplanner_project_id && data.floorplanner_project_id.length) {
            return data.floorplanner_project_id;
        }

        return '';
    }
    /**
     * get first image from first stage for floorplan's gallery
     * @param {any} stages
     * @returns {string} initial image url
     */
    const getInitialImage = (stages) => {
        if (stages.length && stages[0].markers[0]) {
            const initialIndex = stages[0].beginning
                ? stages[0].beginning
                : 0;
            return stages[0].markers[initialIndex].file_name;
        }
        return '';
    }


    const getFloorplanData = (floorplan) => {
        if (!floorplan) {
            return {
                ...state.floorplan
            }
        }

        return {
            ...state.floorplan,
            imagesPath: floorplan.base_path,
            stages: floorplan.stages,
            activeSlideName: getInitialImage(floorplan.stages)
        }
    }

    const getMapData = (map) => {
        if (!map) {
            return {
                ...state.map,
                showMap: false
            }
        }

        return {
            ...state.map,
            location: {
                ...state.map.location,
                lat: parseFloat(map.location.lat) ? parseFloat(map.location.lat) : 0,
                lon: parseFloat(map.location.lon) ? parseFloat(map.location.lon) : 0
            },
            info: {
                ...state.map.info,
                zoom: parseFloat(map.mapInfo.map_zoom_level),
                defaultStreetView: map.mapInfo.defaultStreetView,
                type: map.mapInfo.type
            },
            showMap: true
        }
    }

    switch (action.type) {
        case appConsts.GET_TOUR_FULFILLED:
            const data = action.payload;
            return {
                ...state,
                app: {
                    ...state.app,
                    getTourFulfilled: true,
                    isDrafted: !!+data.is_draft,
                    isLogined: data.is_logined,
                    isActive: !!+data.is_active,
                    theme: data.sps_theme,
                    componentsArray: data.story_components_order,
                    buttonsVisibility: {
                        ...state.app.buttonsVisibility,
                        threeDModelBtnVisible: !!+getThreeDModelUrl(data).length,
                        floorplanBtnVisible: data.floorplan,
                        matterportBtnVisible: !!+data.matterport.data.length,
                        videoBtnVisible: !!+data.videos.data.length,
                        mapBtnVisible: !!+data.buttons_state.media_amap,
                        shareBtnVisible: !!+data.buttons_state.tools_bookmarks,
                        walkthruBtnVisible: !!+data.walkthru_video.length,
                        infoBtnVisible: !!+data.buttons_state.media_minfo,
                        descriptionBtnVisible: !!data.description,
                        documentsBtnVisibility: !!+data.documents,
                        locationBtnVisibility: !!data.map,
                        claimHomeBtnVisibility: !!data.claim_url, // showFeatures
                        propertyDetailsBtnVisibility: !!+data.buttons_state.showFeatures,
                        presentedByBtnVisibility: (
                                data.agent.email
                                && data.agent.name
                                && data.agent.company
                            ) && (
                                data.colisterInfo.email
                                && data.colisterInfo.name
                                && data.colisterInfo.company
                            )
                    },
                    orderBottomLogo: {
                        ...state.app.orderBottomLogo,
                        logo: data.orderBottomLogo.logo,
                        url: data.orderBottomLogo.url
                    },
                    poweredByLogo: data.powered_by_logo,
                    housingLogoVisible: data.buttons_state.show_housing,
                    realtorLogoVisible: data.buttons_state.show_realtor,
                    titleCompany: data.title_company,
                    tourMode: new URL(window.location.href).pathname.split('/').pop(),
                    reCaptcha_key: data.reCaptcha_key
                },
                property: {
                    ...state.property,
                    info: {
                        ...state.property.info,
                        address: {
                            ...state.property.info.address,
                            full: data.address,
                            city: data.city,
                            state: data.state,
                            street: data.street,
                            zip: data.zip
                        },
                        description: data.description,
                        descriptionPlain: data.description_plain,
                        title: data.tour_title,
                        price: {
                            ...state.property.info.price,
                            type: data.propertyInfo.price.type,
                            value: parseFloat(data.propertyInfo.price.value),
                            from: parseFloat(data.propertyInfo.price.from_value),
                            to: parseFloat(data.propertyInfo.price.to_value)
                        },
                        sqft: {
                            ...state.property.info.sqft,
                            type: data.propertyInfo.sqft.type,
                            value: parseFloat(data.propertyInfo.sqft.value),
                            from: parseFloat(data.propertyInfo.sqft.from_value),
                            to: parseFloat(data.propertyInfo.sqft.to_value)
                        },
                        propertyType: data.propertyInfo.property_type,
                        listingType: data.propertyInfo.listing_type,
                        listingStatus: data.listing_status,
                        listingPricePeriod: data.listing_price_period,
                        listingPricePeriodFrom: data.listing_price_period_from,
                        listingPricePeriodTo: data.listing_price_period_to,
                        listingId: data.mls_id,
                        lotSize: data.propertyInfo.lot_size,
                        basementType: data.propertyInfo.basement_type,
                        garageBays: parseFloat(data.propertyInfo.num_of_garage_bays),
                        additionalGarageBays: data.propertyInfo.num_of_add_garage_bays,
                        bedrooms: parseFloat(data.propertyInfo.num_of_bedrooms),
                        bathsAll: parseFloat(data.propertyInfo.num_of_all_baths),
                        baths: parseFloat(data.propertyInfo.num_of_baths),
                        halfBath: parseFloat(data.propertyInfo.num_of_half_baths),
                        twoThirdBaths: parseFloat(data.propertyInfo.num_of_two_thirds_baths),
                        quarterBaths: parseFloat(data.propertyInfo.num_of_quarter_baths),

                        floorsAboveGround: parseFloat(data.propertyInfo.num_of_floors_above_ground),
                        yearBuilt: data.propertyInfo.year_built
                    },
                    features: {
                        exterior: data.propertyFeatures.exterior,
                        interior:data.propertyFeatures.interior
                    },
                    additionalResources: data.additional_resources,
                    documents: data.documents,
                    disclaimer: data.disclaimer
                },
                agent: {
                    agentInfo: {
                        id: parseInt(data.agent.member_id),
                        name: data.agent.name,
                        company: data.agent.company,
                        phone: data.agent.phone,
                        mobilePhone: data.agent.mobile_phone,
                        businessPhone: data.agent.business_phone,
                        fax: data.agent.fax,
                        title: data.agent.title,
                        designations: data.agent.designations,
                        license_number: data.agent.license_number,
                        email: data.agent.email,
                        website: data.agent.website,
                        orig_website: data.agent.orig_website,
                        logo: data.agent.logo,
                        logoTime: data.S3Information.logoTime,
                        photoTime: data.S3Information.photoTime,
                        photo: data.agent.photo,
                        address: data.agent.agent_company_address,
                        showFpoBrand: data.show_fpo_brand
                    },
                    colisterInfo: {
                        id: parseInt(data.colisterInfo.member_id),
                        name: data.colisterInfo.name || '',
                        company: data.colisterInfo.company || '',
                        phone: data.colisterInfo.phone || '',
                        mobilePhone: data.colisterInfo.mobile_phone || '',
                        businessPhone: data.colisterInfo.business_phone || '',
                        fax: data.colisterInfo.fax || '',
                        title: data.colisterInfo.title || '',
                        designations: data.colisterInfo.designations || '',
                        license_number: data.colisterInfo.license_number || '',
                        email: data.colisterInfo.email || '',
                        website: data.colisterInfo.website || '',
                        orig_website: data.colisterInfo.orig_website || '',
                        logo: data.colisterInfo.logo || '',
                        photo: data.colisterInfo.photo || '',
                        address: data.colisterInfo.agent_company_address || ''
                    }
                },
                map: getMapData(data.map),
                threeDModel: {
                    ...state.threeDModel,
                    url: getThreeDModelUrl(data)
                },
                video: {
                    ...state.video,
                    videos: data.videos.data,
                    videoType: data.videos.type,
                    embedTitle: data.videos.title,
                    walkthruVideo: data.walkthru_video || ''
                },
                matterport: {
                    ...state.matterport,
                    matterport: data.matterport.data,
                    matterportType: data.matterport.type,
                    matterportTitle: data.matterport.title || ''
                },
                floorplan: getFloorplanData(data.floorplan),
                modals: {
                    ...state.modals,
                    modalDraftIsOpen: !!+data.is_draft
                },
                gallery: {
                    ...state.gallery,
                    galleryExists:!!+data.photo_count
                },
                claimHome: {
                    ...state.claimHome,
                    url: data.claim_url
                },
                featured: {
                    ...state.featured,
                },
                hero: {
                    ...state.hero,
                    yearBuilt: data.heroInfo.year_built,
                    bedrooms: parseFloat(data.heroInfo.num_of_bedrooms),
                    baths: parseFloat(data.heroInfo.num_of_baths),
                    bathsAll: parseFloat(data.heroInfo.num_of_all_baths),
                    halfBath: parseFloat(data.heroInfo.num_of_half_baths),
                    garageBays: parseFloat(data.heroInfo.num_of_garage_bays),
                    lotSize: data.heroInfo.lot_size,
                    price: {
                        ...state.hero.price,
                        type: data.heroInfo.price.type,
                        value: parseFloat(data.heroInfo.price.value),
                        from: parseFloat(data.heroInfo.price.from_value),
                        to: parseFloat(data.heroInfo.price.to_value)
                    },
                    sqft: {
                        ...state.hero.sqft,
                        type: data.heroInfo.sqft.type,
                        value: parseFloat(data.heroInfo.sqft.value),
                        from: parseFloat(data.heroInfo.sqft.from_value),
                        to: parseFloat(data.heroInfo.sqft.to_value)
                    },
                }
            }
            case appConsts.POST_MESSAGE_EVENT:
                return {
                    ...state,
                    app: {
                        ...state.app,
                        theme: action.payload.theme || state.app.theme,
                        backgroundPhoto: {
                            ...state.app.backgroundPhoto,
                            file_name: action.payload.file_name || state.app.backgroundPhoto.file_name
                        }
                    },
                    property: {
                        ...state.property,
                    info: {
                        ...state.property.info,
                        title: action.payload.title || state.property.info.title,
                        listingStatus: action.payload.listingStatus === undefined ? state.property.info.listingStatus : action.payload.listingStatus,
                        description: action.payload.description || state.property.info.description,
                        price: action.payload.price !== undefined ? action.payload.price : state.property.info.price,
                        sqft: action.payload.sqft !== undefined ? action.payload.sqft : state.property.info.sqft,
                        bedrooms: action.payload.bedrooms !== undefined ? action.payload.bedrooms : state.property.info.bedrooms,
                        baths: action.payload.baths !== undefined ? action.payload.baths : state.property.info.baths,
                        lotSize: action.payload.lotSize !== undefined ? action.payload.lotSize : state.property.info.lotSize,
                        halfBath: action.payload.halfBath !== undefined ? action.payload.halfBath : state.property.info.halfBath,
                        // descriptionPlain: action.payload.description || state.property.info.descriptionPlain
                    }
                }
            }
        default:
            break;
    }

    if (action.type === '') {
        console.log(action.payload);
    }
    return appReducer(state, action)
}

export default rootReducer;
